import Swiper from 'swiper';
import { A11y, FreeMode, Mousewheel, Navigation, Thumbs, Zoom } from 'swiper/modules';
import '@styles/front/css/product-gallery.scss';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1;

function initializeMainGallery( gallery: HTMLElement ): Swiper | null {
	const mainElement = gallery.querySelector<HTMLElement>( '[data-mlc-gallery-main]' );
	const thumbsElement = gallery.querySelector<HTMLElement>( '[data-mlc-gallery-thumbs]' );
	const previousButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-gallery-previous]' );
	const nextButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-gallery-next]' );
	const thumbnailButtons = Array.from( gallery.querySelectorAll<HTMLButtonElement>( '[data-mlc-gallery-thumb]' ) );
	const imageButtons = Array.from( gallery.querySelectorAll<HTMLButtonElement>( '[data-mlc-gallery-open]' ) );

	if ( ! mainElement || ! thumbsElement || ! previousButton || ! nextButton || thumbnailButtons.length < 2 ) {
		return null;
	}

	const thumbsSwiper = new Swiper( thumbsElement, {
		modules: [ A11y, FreeMode, Mousewheel ],
		a11y: {
			enabled: true,
			containerMessage: 'Product image thumbnails',
			itemRoleDescriptionMessage: 'Thumbnail',
			slideLabelMessage: '{{index}} of {{slidesLength}}',
		},
		freeMode: {
			enabled: true,
			momentumBounce: false,
		},
		mousewheel: {
			forceToAxis: true,
			releaseOnEdges: true,
		},
		slidesPerView: 'auto',
		spaceBetween: 10,
		watchOverflow: true,
		watchSlidesProgress: true,
	} );

	const mainSwiper = new Swiper( mainElement, {
		modules: [ A11y, Navigation, Thumbs ],
		a11y: {
			enabled: true,
			containerMessage: 'Main product image viewer',
			itemRoleDescriptionMessage: 'Product image',
			nextSlideMessage: 'Show next image',
			prevSlideMessage: 'Show previous image',
			slideLabelMessage: '{{index}} of {{slidesLength}}',
		},
		grabCursor: true,
		navigation: {
			nextEl: nextButton,
			prevEl: previousButton,
		},
		spaceBetween: 16,
		speed: 280,
		thumbs: {
			autoScrollOffset: 1,
			swiper: thumbsSwiper,
		},
	} );

	const keepActiveThumbnailVisible = ( index: number ): void => {
		window.requestAnimationFrame( () => {
			const activeSlide = thumbsSwiper.slides[ index ];
			if ( ! activeSlide ) {
				return;
			}

			const viewport = thumbsElement.getBoundingClientRect();
			const thumbnail = activeSlide.getBoundingClientRect();
			if ( thumbnail.left < viewport.left || thumbnail.right > viewport.right ) {
				thumbsSwiper.slideTo( index, 220 );
			}
		} );
	};

	const synchronizeThumbnails = (): void => {
		thumbnailButtons.forEach( ( button, index ) => {
			const isActive = index === mainSwiper.activeIndex;
			button.setAttribute( 'aria-pressed', String( isActive ) );
			button.toggleAttribute( 'aria-current', isActive );
		} );
		keepActiveThumbnailVisible( mainSwiper.activeIndex );
	};

	thumbnailButtons.forEach( ( button, index ) => {
		button.addEventListener( 'click', () => mainSwiper.slideTo( index ) );
	} );

	mainElement.addEventListener( 'keydown', ( event ) => {
		let targetIndex: number | null = null;
		switch ( event.key ) {
			case 'ArrowLeft': targetIndex = mainSwiper.activeIndex - 1; break;
			case 'ArrowRight': targetIndex = mainSwiper.activeIndex + 1; break;
			case 'Home': targetIndex = 0; break;
			case 'End': targetIndex = mainSwiper.slides.length - 1; break;
			default: return;
		}

		event.preventDefault();
		const clampedIndex = Math.max( 0, Math.min( targetIndex, mainSwiper.slides.length - 1 ) );
		const shouldMoveFocus = event.target instanceof HTMLButtonElement && event.target.hasAttribute( 'data-mlc-gallery-open' );
		const focusImage = (): void => imageButtons[ clampedIndex ]?.focus( { preventScroll: true } );
		if ( shouldMoveFocus && clampedIndex !== mainSwiper.activeIndex ) {
			mainSwiper.once( 'transitionEnd', focusImage );
		}
		mainSwiper.slideTo( clampedIndex );
		if ( shouldMoveFocus && clampedIndex === mainSwiper.activeIndex ) {
			focusImage();
		}
	} );

	mainSwiper.on( 'slideChange', synchronizeThumbnails );
	mainSwiper.on( 'transitionEnd', synchronizeThumbnails );
	gallery.classList.add( 'is-ready' );
	synchronizeThumbnails();

	return mainSwiper;
}

function initializeLightbox( gallery: HTMLElement, mainSwiper: Swiper | null ): void {
	const dialog = gallery.querySelector<HTMLDialogElement>( '[data-mlc-gallery-dialog]' );
	const lightboxElement = gallery.querySelector<HTMLElement>( '[data-mlc-gallery-lightbox]' );
	const openButtons = Array.from( gallery.querySelectorAll<HTMLButtonElement>( '[data-mlc-gallery-open]' ) );
	const closeButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-gallery-close]' );
	const previousButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-lightbox-previous]' );
	const nextButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-lightbox-next]' );
	const zoomOutButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-zoom-out]' );
	const zoomInButton = gallery.querySelector<HTMLButtonElement>( '[data-mlc-zoom-in]' );
	const zoomStatus = gallery.querySelector<HTMLOutputElement>( '[data-mlc-zoom-status]' );

	if ( ! dialog || ! lightboxElement || ! closeButton || ! zoomOutButton || ! zoomInButton || ! zoomStatus || openButtons.length === 0 ) {
		return;
	}

	let lightboxSwiper: Swiper | null = null;
	let lastOpener: HTMLButtonElement | null = null;

	const synchronizeZoomControls = ( scale = lightboxSwiper?.zoom.scale ?? MIN_ZOOM ): void => {
		const normalizedScale = Math.max( MIN_ZOOM, Math.min( scale, MAX_ZOOM ) );
		zoomStatus.value = `${ Math.round( normalizedScale * 100 ) }%`;
		zoomStatus.textContent = zoomStatus.value;
		zoomOutButton.disabled = normalizedScale <= MIN_ZOOM;
		zoomInButton.disabled = normalizedScale >= MAX_ZOOM;
	};

	const createLightboxSwiper = (): Swiper => {
		const swiper = new Swiper( lightboxElement, {
			modules: [ A11y, Navigation, Zoom ],
			a11y: {
				enabled: true,
				containerMessage: 'Full-size product image gallery',
				itemRoleDescriptionMessage: 'Full-size image',
				nextSlideMessage: 'Show next image',
				prevSlideMessage: 'Show previous image',
				slideLabelMessage: '{{index}} of {{slidesLength}}',
			},
			navigation: previousButton && nextButton ? {
				nextEl: nextButton,
				prevEl: previousButton,
			} : false,
			observer: true,
			observeParents: true,
			speed: 240,
			watchOverflow: true,
			zoom: {
				maxRatio: MAX_ZOOM,
				minRatio: MIN_ZOOM,
				panOnMouseMove: false,
				toggle: true,
			},
		} );

		swiper.on( 'zoomChange', ( _, scale ) => synchronizeZoomControls( scale ) );
		swiper.on( 'slideChange', () => {
			swiper.zoom.out();
			synchronizeZoomControls();
			mainSwiper?.slideTo( swiper.activeIndex );
		} );
		return swiper;
	};

	openButtons.forEach( ( button, buttonIndex ) => {
		button.addEventListener( 'click', () => {
			if ( mainSwiper && ! mainSwiper.allowClick ) {
				return;
			}
			const index = Number.parseInt( button.dataset.mlcGalleryIndex ?? String( buttonIndex ), 10 );
			lastOpener = button;
			dialog.showModal();
			document.body.classList.add( 'mlc-product-lightbox-open' );
			lightboxSwiper ??= createLightboxSwiper();
			lightboxSwiper.update();
			lightboxSwiper.slideTo( Number.isNaN( index ) ? buttonIndex : index, 0, false );
			lightboxSwiper.zoom.out();
			synchronizeZoomControls();
			closeButton.focus();
		} );
	} );

	closeButton.addEventListener( 'click', () => dialog.close() );
	dialog.addEventListener( 'click', ( event ) => {
		if ( event.target === dialog ) {
			dialog.close();
		}
	} );
	dialog.addEventListener( 'close', () => {
		const activeOpener = lightboxSwiper ? openButtons[ lightboxSwiper.activeIndex ] : lastOpener;
		document.body.classList.remove( 'mlc-product-lightbox-open' );
		lightboxSwiper?.zoom.out();
		synchronizeZoomControls();
		window.requestAnimationFrame( () => ( activeOpener ?? lastOpener )?.focus( { preventScroll: true } ) );
	} );

	zoomOutButton.addEventListener( 'click', () => {
		if ( lightboxSwiper ) {
			const targetScale = Math.max( MIN_ZOOM, lightboxSwiper.zoom.scale - ZOOM_STEP );
			targetScale === MIN_ZOOM ? lightboxSwiper.zoom.out() : lightboxSwiper.zoom.in( targetScale );
		}
	} );
	zoomInButton.addEventListener( 'click', () => {
		if ( lightboxSwiper ) {
			lightboxSwiper.zoom.in( Math.min( MAX_ZOOM, lightboxSwiper.zoom.scale + ZOOM_STEP ) );
		}
	} );

	dialog.addEventListener( 'keydown', ( event ) => {
		if ( ! lightboxSwiper ) {
			return;
		}
		switch ( event.key ) {
			case 'ArrowLeft': event.preventDefault(); lightboxSwiper.slidePrev(); break;
			case 'ArrowRight': event.preventDefault(); lightboxSwiper.slideNext(); break;
			case '+':
			case '=': event.preventDefault(); zoomInButton.click(); break;
			case '-':
			case '_': event.preventDefault(); zoomOutButton.click(); break;
		}
	} );
}

function initializeProductGallery( gallery: HTMLElement ): void {
	const mainSwiper = initializeMainGallery( gallery );
	initializeLightbox( gallery, mainSwiper );
}

document.querySelectorAll<HTMLElement>( '[data-mlc-product-gallery]' ).forEach( initializeProductGallery );

export {};
